import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { getMm } from "../../theme/modernMaterialEnterprise";
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useSystemButtonStyles } from "../../hooks/useSystemButtonStyles";
import logo from '../../NewEraLogo.png';
import { openBugReportDialog } from '../../components/BugReportHost';

import { APP_VERSION } from "../../constants/appVersion";

export const VERSION = APP_VERSION;

const VersionDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const {
    primaryButtonSx,
    cancelButtonSx,
    outlineButtonSx,
    secondaryButtonSx,
    destructiveButtonSx,
    destructiveOutlineButtonSx,
    greenSmileyButtonSx,
    redSmileyButtonSx,
    iconActionSx,
    iconActionTableSx,
    iconActionLargeSx,
    toolbarButtonSx,
    dialogPrimarySx,
    dialogCancelSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
    warningButtonSx,
    buttonSx,
  } = useSystemButtonStyles();
  
  const currentYear = new Date().getFullYear();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          boxShadow: 'none',
          border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          backgroundColor: getMm(isDarkMode).surfaceLowest,
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 1,
          fontWeight: 600,
          fontSize: '1.125rem',
          color: getMm(isDarkMode).onSurface,
          borderBottom: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <img
            src={isDarkMode ? logo : '/NewEraBlackLogo.png'}
            alt="New Era Logo"
            style={{ height: 60 }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <Box sx={{ textAlign: 'center', mb: 3, mt: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.125rem',
              color: getMm(isDarkMode).onSurface,
              mb: 1.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {t('version_label')} {VERSION}
          </Typography>
          <Typography
            sx={{
              color: getMm(isDarkMode).onSurfaceVariant,
              fontSize: '0.875rem',
              fontStyle: 'italic',
              mb: 1,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {t('version_latest_release')}
          </Typography>
        </Box>

        <Divider sx={{
          my: 2,
          borderColor: getMm(isDarkMode).outlineVariant
        }} />

        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: getMm(isDarkMode).onSurface,
              mb: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {t('version_legal_information')}
          </Typography>
          <Typography
            sx={{
              color: getMm(isDarkMode).onSurfaceVariant,
              fontSize: '0.875rem',
              lineHeight: 1.6,
              mb: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {t('version_legal_description_1')}
          </Typography>
          <Typography
            sx={{
              color: getMm(isDarkMode).onSurfaceVariant,
              fontSize: '0.875rem',
              lineHeight: 1.6,
              mb: 2,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {t('version_legal_description_2', { year: currentYear })}
          </Typography>
          <Typography
            sx={{
              color: getMm(isDarkMode).onSurfaceVariant,
              fontSize: '0.875rem',
              lineHeight: 1.6,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {t('version_legal_description_3')}
          </Typography>
        </Box>

        <Divider sx={{
          my: 2,
          borderColor: getMm(isDarkMode).outlineVariant
        }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              color: getMm(isDarkMode).onSurface,
              mb: 1,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {t('version_need_help')}
          </Typography>
          <Button
            type="button"
            variant="text"
            onClick={() => {
              onClose();
              openBugReportDialog();
            }}
            sx={{
              display: 'block',
              mx: 'auto',
              mb: 1,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: getMm(isDarkMode).onSurfaceVariant,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              textDecoration: 'underline',
            }}
          >
            {t('bug_report_title')}
          </Button>
          <Box
            component="a"
            href="mailto:support@neweracss.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: getMm(isDarkMode).onSurfaceVariant,
              fontSize: '0.875rem',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': {
                color: getMm(isDarkMode).onSurfaceVariant
              }
            }}
          >
            support@neweracss.com
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <Button variant="outlined"
          onClick={onClose}
         
          sx={toolbarButtonSx}
        >
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionDialog;
